import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | failed
  const [tries, setTries] = useState(0);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const check = async (attempt) => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState("paid");
          // refresh user
          try {
            const me = await api.get("/auth/me");
            localStorage.setItem("realtify_user_email", me.data.email);
          } catch {
            /* ignore */
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("failed");
          return;
        }
        if (attempt >= 8) {
          setState("pending");
          return;
        }
        setTries(attempt + 1);
        setTimeout(() => check(attempt + 1), 2000);
      } catch {
        if (attempt >= 3) {
          setState("failed");
        } else {
          setTimeout(() => check(attempt + 1), 2000);
        }
      }
    };

    check(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6" data-testid="billing-success-page">
      <div className="card-flat p-10 max-w-md w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={32} />
            <h2 className="font-display text-2xl font-bold mt-4">
              Confirming payment&hellip;
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              This usually takes a few seconds. Attempt {tries + 1}.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2
              className="mx-auto text-[hsl(var(--success))]"
              size={40}
              data-testid="payment-success-icon"
            />
            <h2 className="font-display text-3xl font-bold mt-4">
              You&apos;re all set.
            </h2>
            <p className="text-muted-foreground mt-2">
              Your plan is active. Go analyze a deal &mdash; you saved room for the
              next $25K wholesale fee.
            </p>
            <div className="flex gap-2 mt-6">
              <Link
                to="/lookup"
                data-testid="go-to-lookup"
                className="flex-1 app-btn app-btn-primary h-11"
              >
                Analyze a property
              </Link>
              <Link
                to="/dashboard"
                data-testid="go-to-dashboard"
                className="flex-1 app-btn app-btn-ghost h-11"
              >
                Dashboard
              </Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto" size={32} />
            <h2 className="font-display text-2xl font-bold mt-4">
              Payment still processing
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              Your bank is still confirming. You&apos;ll get an email once it&apos;s done.
            </p>
            <button
              onClick={() => nav("/")}
              data-testid="pending-back-home"
              className="mt-6 app-btn app-btn-primary"
            >
              Back to dashboard
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto text-destructive" size={40} />
            <h2 className="font-display text-2xl font-bold mt-4">
              Payment couldn&apos;t be confirmed
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              We couldn&apos;t verify this checkout session. Please try again.
            </p>
            <Link
              to="/pricing"
              data-testid="try-again-link"
              className="mt-6 inline-flex app-btn app-btn-primary"
            >
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
